import {
  DocumentReferenceResponse,
  useGetApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdThumbnailsQuery,
} from "api/generatedApi";
import { useEffect, useMemo, useState } from "react";

const AllowedImageExtensions = [".png", ".jpg", ".jpeg"] as const;

export interface ThumbnailEntry {
  key: string;
  imageUrl: string;
  altText: string;
  documentId?: string;
  pendingFile?: File;
  uploadedTimestamp?: string;
}

interface Args {
  projectId: string;
  agreementId: string;
  documents: DocumentReferenceResponse[];
  pendingUploads: File[];
}

interface Result {
  thumbnails: ThumbnailEntry[];
  isLoading: boolean;
}

export const useExtraWorkAgreementThumbnails = ({
  projectId,
  agreementId,
  documents,
  pendingUploads,
}: Args): Result => {
  const skip = !projectId || !agreementId;
  const { data, isLoading } = useGetApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdThumbnailsQuery(
    { projectId, extraWorkAgreementId: agreementId },
    { skip }
  );

  const savedThumbnails = useMemo<ThumbnailEntry[]>(() => {
    const docById = new Map(documents.filter((d) => d.documentId).map((d) => [d.documentId!, d]));

    const result: ThumbnailEntry[] = [];
    for (const thumbnail of data?.thumbnails ?? []) {
      if (!thumbnail.documentId || !thumbnail.thumbnailDataUri) continue;
      const doc = docById.get(thumbnail.documentId);
      if (!doc) continue;
      result.push({
        key: thumbnail.documentId,
        imageUrl: thumbnail.thumbnailDataUri,
        altText: doc.name ?? thumbnail.documentId,
        documentId: thumbnail.documentId,
        uploadedTimestamp: doc.uploadedTimestamp,
      });
    }
    return result;
  }, [data, documents]);

  // Convert pending uploads into blob URLs so freshly-uploaded files preview instantly.
  const [pendingPreviews, setPendingPreviews] = useState<ThumbnailEntry[]>([]);
  useEffect(() => {
    const previews: ThumbnailEntry[] = pendingUploads
      .filter((file) => AllowedImageExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)))
      .map((file) => ({
        key: `pending-${file.name}-${file.size}-${file.lastModified}`,
        imageUrl: URL.createObjectURL(file),
        altText: file.name,
        pendingFile: file,
        uploadedTimestamp: new Date(file.lastModified).toISOString(),
      }));
    setPendingPreviews(previews);
    return () => {
      previews.forEach((entry) => URL.revokeObjectURL(entry.imageUrl));
    };
  }, [pendingUploads]);

  return {
    thumbnails: [...savedThumbnails, ...pendingPreviews],
    isLoading,
  };
};
